/**
 * useContradictions — TanStack Query hook for `contradictions.contradictions.list`.
 *
 * Fetches LLM-derived narrative contradictions between official WHO/CDC
 * statements and press-reported actions. Every row carries two provenance
 * URLs verified at ingest time.
 *
 * Date fields arrive as ISO strings over the wire and are normalised to
 * Date objects here.
 *
 * Prompt #09 (timeline-contradictions-and-unanswered-questions).
 */

import { useQuery } from "@tanstack/react-query";

export type ContradictionItem = {
  id: string;
  datedAt: Date;
  severity: "HIGH" | "MEDIUM";
  officialStatement: string;
  actualAction: string;
  officialSourceUrl: string;
  actionSourceUrl: string;
  verifiedAt: Date;
};

type RawContradiction = Omit<ContradictionItem, "datedAt" | "verifiedAt"> & {
  datedAt: string;
  verifiedAt: string;
};

function normalise(raw: RawContradiction): ContradictionItem {
  return {
    ...raw,
    datedAt: new Date(raw.datedAt),
    verifiedAt: new Date(raw.verifiedAt),
  };
}

async function fetchContradictions(
  limit: number,
  apiBaseUrl?: string,
): Promise<ContradictionItem[]> {
  const base = (
    apiBaseUrl ??
    (typeof import.meta !== "undefined"
      ? (import.meta.env?.VITE_API_URL as string | undefined)
      : undefined) ??
    ""
  ).replace(/\/+$/u, "");

  const encoded = encodeURIComponent(JSON.stringify({ json: { limit } }));
  const url = `${base}/api/trpc/contradictions.contradictions.list?input=${encoded}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });

  if (!res.ok) {
    throw new Error(`contradictions.contradictions.list failed: ${res.status}`);
  }

  const json = (await res.json()) as {
    result?: { data?: { json?: RawContradiction[] } | RawContradiction[] };
  };

  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: RawContradiction[] }).json
      : (inner as RawContradiction[] | undefined);

  if (!data) throw new Error("contradictions.list: malformed response");
  return data.map(normalise);
}

export function useContradictions(opts?: {
  limit?: number;
  apiBaseUrl?: string;
}) {
  const limit = opts?.limit ?? 25;

  return useQuery<ContradictionItem[]>({
    queryKey: ["contradictions", "list", limit],
    queryFn: () => fetchContradictions(limit, opts?.apiBaseUrl),
    staleTime: 60 * 60 * 1000, // 1 hour — matches the extractor cadence
    retry: 2,
  });
}
